import ActivityPoints from "@components/ActivityPoints";
import BrandLogo from "@components/BrandLogo";
import Button from "@components/Button";
import { Navigate, useNavigate, useParams } from "react-router";
import { QueryClient, useQuery } from "@tanstack/react-query";
import StickyButton from "../../components/StickyButton";
import "./style.scss";
import { sendEvent } from "../../Helpers";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import ActivityType from "@components/ActivityType";
import {
  getModuleValue,
  parseCMSBold,
  parseHTMLToText,
  useWindowSize,
} from "../../Helpers";
import {
  ACTIVATION_CACHE_TIME,
  ACTIVATION_STALE_TIME,
  getActivationDescribe,
} from "../../api/activation";
import DOMPurify from "dompurify";

export default function IntroTemplate() {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const XGA = width >= 1020;
  const navigate = useNavigate();
  const isUltraInsider =
    location.pathname.includes("/subscription") ||
    location.pathname.includes("/ultra-insider") ||
    location.pathname.includes("/ultrainsider");

  const { data, isError, isLoading } = useQuery({
    queryKey: ["activation-describe", ident],
    queryFn: () =>
      getActivationDescribe({
        activation: {
          ident: ident,
          withSolutions: true,
        },
      }),
    retry: false,
    cacheTime: ACTIVATION_CACHE_TIME,
  });

  const activationQuestionsQuery = useQuery({
    queryKey: ["game-questions", ident],
    queryFn: async () => {
      let data;
      try {
        data = await import(`./Activities/Idents/${ident}/data.js`);
        return data.gameData;
      } catch (e) {
        console.error(e);
        throw new Error("Activation doesn't exist");
      }
    },
    staleTime: 1000 * 60 * 60,
    cacheTime: ACTIVATION_CACHE_TIME,
  });

  const isUltraInsiderVuseFragrance =
    ident === "ultrainsider_vuse_fragrance_2025";

  const vuseFragranceQuery = useQuery({
    queryKey: ["activation-describe", "vuse_fragrance_2025"],
    queryFn: () =>
      getActivationDescribe(
        {
          activation: {
            ident: "vuse_fragrance_2025",
            withSolutions: true,
          },
        },
        false,
      ),
    enabled: isUltraInsiderVuseFragrance,
    suspense: false,
    retry: false,
    staleTime: ACTIVATION_STALE_TIME,
    cacheTime: ACTIVATION_CACHE_TIME,
  });

  const isCompleted = isUltraInsiderVuseFragrance
    ? vuseFragranceQuery.data?.completed
    : undefined;

  if (
    (activationQuestionsQuery.isError && !activationQuestionsQuery.isLoading) ||
    (isError && !isLoading)
  ) {
    return <Navigate to="/activities" replace />;
  }

  if (ident.includes("poll_1")) {
    return <Navigate to={`/activities/${ident}/gra`} replace />;
  }

  if (activationQuestionsQuery.data?.contest) {
    return <Navigate to={`/contest/${ident}`} replace />;
  }

  if (data?.completed) {
    return <Navigate to={`/activities`} replace />;
  }

  const customBtnTextStart = getModuleValue(
    data.content?.modules,
    "activity_button",
    "metadata.titleStart",
  );
  const customBtnUrlStart = getModuleValue(
    data.content?.modules,
    "activity_button",
    "metadata.urlStart",
  );

  const getUserSystem = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/windows phone/i.test(userAgent)) return "Windows Phone";
    if (/win/i.test(userAgent)) return "Windows";
    if (/android/i.test(userAgent)) return "Android";
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return "iOS";
    if (/mac/i.test(userAgent)) return "macOS";
    if (/linux/i.test(userAgent)) return "Linux";
    return "Unknown";
  };

  const handleStart = (e) => {
    e.preventDefault();
    const system = getUserSystem();
    sendEvent(`${ident}_START`);
    sendEvent(`${ident}_SYS_${system}`);

    let targetUrl = "";
    if (!data.completed && customBtnUrlStart) {
      targetUrl = customBtnUrlStart;
    } else if (data.completed) {
      targetUrl = `/activities`;
    } else {
      targetUrl = `/activities/${ident}/gra`;
    }

    setTimeout(() => {
      navigate(targetUrl);
    }, 200);
  };

  const startVuseFragrance2025 = () => {
    navigate(`/activities/vuse_fragrance_2025`);
    sendEvent(`${ident}_START`);
  };

  const startVelomoments2025 = () => {
    sessionStorage.setItem("halloween2025_start", "true");
    sessionStorage.setItem("hideEasterEggBar", "false");
    window.dispatchEvent(new Event("hideEasterEggBarUpdated"));
    window.dispatchEvent(new Event("velomomentsUpdated"));
    navigate("/");
    sendEvent(`${ident}_START`);
  };

  return (
    <section
      className={`activities-intro-page ${
        isUltraInsider ? "ultra-insider" : ""
      } ${ident}`}
    >
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(parseCMSBold(data?.content?.description)),
            }}
          ></p>
        </PagesHeader>
        <div className="desktop-wrapper">
          <div className="activity-img">
            <img
              src={getModuleValue(
                data.content?.modules,
                "hero_image",
                "file.url",
              )}
              alt={getModuleValue(
                data.content?.modules,
                "hero_image",
                "metadata.imgDesc",
              )}
              className="mobile-img"
            />
            <img
              src={getModuleValue(
                data.content?.modules,
                "hero_image",
                "file.url",
              )}
              alt={getModuleValue(
                data.content?.modules,
                "hero_image",
                "metadata.imgDesc",
              )}
              className="desktop-img"
            />
            {!!data.payout && (
              <ActivityPoints
                className={`${isUltraInsider ? "ultra-insider" : ""} `}
                desktopBig={ident.includes("konkurs_")}
              >
                {data.payout}
              </ActivityPoints>
            )}

            <BrandLogo brands={data.brands} />
            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button
                    action={
                      isUltraInsiderVuseFragrance && isCompleted === false
                        ? startVuseFragrance2025
                        : ident === "halloween2025"
                          ? startVelomoments2025
                          : handleStart
                    }
                    disabled={
                      ident.includes("konkurs_")
                        ? false
                        : data.completed || data.expired
                    }
                    btnClass={`yellow ${isUltraInsider ? "ultra-insider" : ""} `}
                  >
                    {isUltraInsiderVuseFragrance && isCompleted === false
                      ? "Zur Aktivität"
                      : !data.completed && customBtnTextStart
                        ? customBtnTextStart
                        : data.completed
                          ? "Aktivität abgeschlossen"
                          : "Los geht's"}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
          <div className="main-content">
            <div>
              <p
                className="desc"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(getModuleValue(
                    data.content?.modules,
                    "intro",
                    "custom",
                  )),
                }}
              ></p>
              {isUltraInsiderVuseFragrance && isCompleted === false && (
                <p className="desc">
                  <br />
                  <br />
                  <br />
                  Als Ultra Insider ist diese Verlosung
                  <strong>
                    {" "}
                    exklusiv für dich.
                    <br /> Bitte schließe zuerst die dazugehörige Aktivität ab
                  </strong>{" "}
                  – und komm danach zurück, um an der Verlosung teilzunehmen.
                </p>
              )}
              {!activationQuestionsQuery?.data.hideHowToBox && (
                <div className="how-to-box">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(getModuleValue(
                        data.content?.modules,
                        "how_to_use_activity",
                        "custom",
                      )),
                    }}
                  ></p>
                </div>
              )}
            </div>
            {activationQuestionsQuery.data?.introDes && (
              <p
                className="desc-custom"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(activationQuestionsQuery.data.introDes),
                }}
              ></p>
            )}
          </div>
        </div>
        {!XGA && (
          <StickyButton
            action={
              isUltraInsiderVuseFragrance && isCompleted === false
                ? startVuseFragrance2025
                : ident === "halloween2025"
                  ? startVelomoments2025
                  : handleStart
            }
            disabled={
              ident.includes("konkurs_")
                ? false
                : data.completed || data.expired
            }
          >
            {isUltraInsiderVuseFragrance && isCompleted === false
              ? "Zur Aktivität"
              : !data.completed && customBtnTextStart
                ? customBtnTextStart
                : data.completed
                  ? "Aktivität abgeschlossen"
                  : "Los geht's"}
          </StickyButton>
        )}
      </div>
    </section>
  );
}
